/**
 * Shared Stripe confirmPayment helpers for checkout and invoice Alpine flows.
 * Kept free of Alpine/Livewire so the race/error classification can be unit-tested.
 */

export function shouldApplyCreditsBeforeStripeConfirm({
	useCredits,
	remainingAfterCredits,
	creditsAppliedAmount,
} = {}) {
	const remaining = Number(remainingAfterCredits);
	const applied = Number(creditsAppliedAmount);

	if (! Number.isFinite(remaining) || remaining <= 0) {
		return false;
	}

	// Gate on Locked `creditsAppliedAmountValue` (via readCreditApplyFlagsFromWire).
	// Alpine `creditsAppliedAmount` stays 0, but the wire mirror is authoritative.
	// A Livewire applyCreditsAndPay() round trip with $0 credits morphs the
	// checkout/invoice DOM and destroys mounted Stripe/PayPal iframes.
	if (! Number.isFinite(applied) || applied <= 0.009) {
		return false;
	}

	return Boolean(useCredits);
}

function firstFiniteNumber(...values) {
	for (const value of values) {
		const numeric = Number(value);
		if (Number.isFinite(numeric)) {
			return numeric;
		}
	}

	return 0;
}

/**
 * Prefer Locked public mirrors (`*Value`) over Computed `$wire.get` names
 * and Alpine copies. Checkout Alpine `creditsAppliedAmount` stays 0 without
 * `creditsAppliedAmountValue`.
 */
export function readCreditApplyFlagsFromWire(wireGet, alpineFallback = {}) {
	const get = typeof wireGet === 'function' ? wireGet : () => undefined;

	const useCredits = get('useCredits');

	return {
		useCredits: typeof useCredits === 'undefined' ? Boolean(alpineFallback.useCredits) : Boolean(useCredits),
		remainingAfterCredits: firstFiniteNumber(
			get('remainingAfterCreditsValue'),
			get('remainingAfterCredits'),
			alpineFallback.remainingAfterCredits,
		),
		creditsAppliedAmount: firstFiniteNumber(
			get('creditsAppliedAmountValue'),
			get('creditsAppliedAmount'),
			alpineFallback.creditsAppliedAmount,
		),
	};
}

/**
 * Livewire applyCreditsAndPay() catches exceptions, so the JS promise can
 * resolve even when credits were not applied. Abort confirmPayment in that
 * case so we do not charge the card against a stale remainder.
 *
 * @param {null | { ok?: boolean, credits_applied?: number, fully_paid?: boolean }} result
 * @param {{ expectedCreditsApplied?: number }} extras
 */
export function shouldAbortStripeConfirmAfterCreditApply(result, extras = {}) {
	if (result == null || typeof result !== 'object') {
		return { abort: true, reason: 'credit_apply_no_result' };
	}

	if (result.ok === false) {
		return { abort: true, reason: 'credit_apply_failed' };
	}

	if (result.fully_paid === true) {
		return { abort: true, reason: 'fully_paid' };
	}

	const expected = Number(extras.expectedCreditsApplied);
	const applied = Number(result.credits_applied);
	if (Number.isFinite(expected) && expected > 0.009) {
		if (! Number.isFinite(applied) || applied + 0.009 < expected) {
			return { abort: true, reason: 'credits_not_applied' };
		}
	}

	return { abort: false, reason: null };
}

export function stripeExceptionMessage(error) {
	if (error == null) {
		return '';
	}

	if (typeof error === 'string') {
		return error;
	}

	const nested = error.error && typeof error.error === 'object' ? error.error : null;

	return String(error.message || nested?.message || error.msg || '');
}

export function isStripeElementsRemountError(error, extras = {}) {
	if (extras.elementsIdentityChanged === true || extras.paymentElementConnected === false) {
		return true;
	}

	const code = String(error?.code || error?.error?.code || '');
	if (code === 'elements_remounted') {
		return true;
	}

	const msg = stripeExceptionMessage(error).toLowerCase();
	if (msg === '') {
		return false;
	}

	return (
		msg.includes('specified element')
		|| msg.includes('ready event has been emitted')
		|| msg.includes('element is not mounted')
		|| msg.includes('has been destroyed')
		|| msg.includes('elements instance has been destroyed')
	);
}

export function stripeSecretFingerprint(secret) {
	if (typeof secret !== 'string' || secret.length < 4) {
		return null;
	}

	return secret.slice(-8);
}

export function stripeElementsSessionChanged({
	stripe,
	elements,
	activeElements,
	activeSecret,
	stillActiveSecret,
	paymentElementConnected = true,
} = {}) {
	return (
		! stripe
		|| ! elements
		|| elements !== activeElements
		|| ! activeSecret
		|| ! stillActiveSecret
		|| stillActiveSecret !== activeSecret
		|| paymentElementConnected === false
	);
}

/**
 * @param {unknown} error
 * @param {string} flow
 * @param {Record<string, unknown>} extras
 */
export function stripeConfirmExceptionPayload(error, flow, extras = {}) {
	const remounted = isStripeElementsRemountError(error, extras);
	const rawMessage = stripeExceptionMessage(error);
	const paymentIntentId =
		extras.paymentIntentId
		?? error?.payment_intent?.id
		?? error?.error?.payment_intent?.id
		?? null;

	const secretFingerprintBefore = extras.secretFingerprintBefore ?? stripeSecretFingerprint(extras.activeSecret);
	const secretFingerprintAfter = extras.secretFingerprintAfter ?? stripeSecretFingerprint(extras.stillActiveSecret);

	const diagnostics = {
		...(extras.diagnostics && typeof extras.diagnostics === 'object' ? extras.diagnostics : {}),
		exceptionName: error?.name ?? error?.type ?? typeof error,
		exceptionMessage: rawMessage ? rawMessage.slice(0, 300) : null,
		secretFingerprintBefore: secretFingerprintBefore ?? null,
		secretFingerprintAfter: secretFingerprintAfter ?? null,
		secretChanged:
			secretFingerprintBefore != null && secretFingerprintAfter != null
				? secretFingerprintBefore !== secretFingerprintAfter
				: extras.secretChanged ?? null,
		elementsIdentityChanged: extras.elementsIdentityChanged ?? null,
		paymentElementConnected: extras.paymentElementConnected ?? null,
	};

	return {
		message: remounted
			? 'Payment form refreshed. Please click Pay again.'
			: (rawMessage || 'Payment failed. Please try again.'),
		gateway: 'stripe',
		code: remounted ? 'elements_remounted' : 'js_exception',
		paymentIntentId,
		flow,
		diagnostics,
	};
}

/**
 * Payer-name box first; fall back to legal name from Payment Element defaults.
 * Required because fields.billingDetails.name is `never` on confirm.
 */
export function resolvedPayerName(defaults = {}, payerName) {
    const fromBox = String(payerName ?? '').trim();
    if (fromBox !== '') {
        return fromBox;
    }

    return String(defaults?.name ?? '').trim();
}

/** Elements-entered billing details take precedence over these confirmation defaults.
 * The external payer-name input is separate from Customer's business billing name.
 */
export function payerBillingDetails(defaults = {}, payerName, enteredPostalCode = null) {
    const name = resolvedPayerName(defaults, payerName);
    if (!name || name.length > 255) {
        throw new Error('Please enter the name of the person paying.');
    }
    const address = { ...(defaults.address ?? {}) };
    if (typeof enteredPostalCode === 'string' && enteredPostalCode.trim() !== '') {
        address.postal_code = enteredPostalCode.trim();
    }
    return {
        name,
        ...(defaults.email ? { email: defaults.email } : {}),
        ...(Object.keys(address).length ? { address } : {}),
    };
}

/**
 * Saved Customer Session methods and Link wallets already carry billing
 * details. Extra payment_method_data on confirm can fail Link/saved-card
 * confirms before Stripe records a PaymentIntent attempt.
 */
export function shouldAttachPayerConfirmBillingDetails(
	selectedSavedPaymentMethodId = null,
	selectedPaymentMethodType = null,
) {
	if (typeof selectedSavedPaymentMethodId === 'string' && selectedSavedPaymentMethodId.startsWith('pm_')) {
		return false;
	}

	const type = String(selectedPaymentMethodType ?? '').trim().toLowerCase();

	return type !== 'link' && ! type.startsWith('link_');
}

/**
 * Attach billing_details.name on confirm for *new* cards only.
 * Payment Element hides the name field (fields.billingDetails.name = never).
 *
 * @param {unknown} [selectedSavedPaymentMethodId]
 * @param {unknown} [selectedPaymentMethodType]
 */
export function payerConfirmParams(
	defaults,
	payerName,
	selectedSavedPaymentMethodId = null,
	selectedPaymentMethodType = null,
) {
	if (! shouldAttachPayerConfirmBillingDetails(selectedSavedPaymentMethodId, selectedPaymentMethodType)) {
		return {};
	}

	return { payment_method_data: { billing_details: payerBillingDetails(defaults, payerName) } };
}

/**
 * Livewire named-event payloads arrive as `{ message }` or `[{ message }]`.
 */
export function livewireEventMessage(event) {
	if (event == null) {
		return '';
	}

	if (typeof event === 'string') {
		return event;
	}

	if (typeof event.message === 'string') {
		return event.message;
	}

	if (Array.isArray(event) && event[0] && typeof event[0].message === 'string') {
		return event[0].message;
	}

	return '';
}

/** @type {Array<{ directive: string, blockedURI: string }>} */
const paymentCspViolations = [];

export function resetPaymentCspViolationsForTests() {
	paymentCspViolations.length = 0;
}

export function isPaymentCspHost(blockedURI) {
	const uri = String(blockedURI || '').toLowerCase();

	return (
		uri.includes('stripe.com')
		|| uri.includes('stripe.network')
		|| uri.includes('link.com')
		|| uri.includes('hcaptcha.com')
	);
}

export function recordPaymentCspViolation(event) {
	const directive = String(event?.effectiveDirective || event?.violatedDirective || '');
	const uri = String(event?.blockedURI || '');
	const disposition = String(event?.disposition || 'enforce');

	if (disposition !== 'enforce') {
		return null;
	}

	const tracked = (
		directive.startsWith('script-src')
		|| directive.startsWith('frame-src')
		|| directive.startsWith('connect-src')
		|| directive.startsWith('style-src')
	);

	if (! tracked || ! isPaymentCspHost(uri)) {
		return null;
	}

	const row = {
		directive,
		blockedURI: uri.slice(0, 300),
	};
	paymentCspViolations.push(row);
	if (paymentCspViolations.length > 12) {
		paymentCspViolations.shift();
	}

	return row;
}

export function paymentCspViolationSnapshot() {
	return paymentCspViolations.slice();
}
